import { useState, useEffect, useCallback, useMemo } from "react";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { normalizarId } from "../../hooks/useScope";
import { useTranslation } from "react-i18next"
import { resolveDateLocale } from "../../i18n"
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Close as CloseIcon
} from "@mui/icons-material";
import EntityList from "../../components/EntityList"
import type { FiltroCampo } from "../../components/EntityList";
import api from "../../services/api";
import { Access, Tenant } from "@/src/interfaces";
import AccessModal from "../../components/AccessModal";
import { useCryptoNavigation } from "../../App";
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery";

/**
 * Converte uma data vinda do backend para um objeto Date.
 * O servidor envia o horário em UTC, mas sem fuso explícito — tanto no formato
 * "DD/MM/YYYY HH:MM" quanto em ISO/SQL sem o sufixo 'Z'. Tratamos ambos como UTC
 * (mesma convenção do app Flutter), para que a exibição e a ordenação fiquem
 * corretas no fuso do usuário.
 */
export function parseServerDate(raw?: string | null): Date | null {
  if (!raw || raw === "null") return null;
  const s = raw.trim();

  // Formato brasileiro: DD/MM/YYYY HH:MM(:SS)? (em UTC)
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})[\s,]+(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (br) {
    const [, dd, mm, yyyy, HH, MM, SS] = br;
    const date = new Date(Date.UTC(+yyyy, +mm - 1, +dd, +HH, +MM, +(SS ?? 0)));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // ISO/SQL: normaliza "YYYY-MM-DD HH:MM:SS" -> "...T..." e adiciona 'Z' se faltar fuso
  let iso = s.includes(" ") && !s.includes("T") ? s.replace(" ", "T") : s;
  const hasTimezone = /[zZ]$/.test(iso) || /[+-]\d{2}:\d{2}$/.test(iso);
  if (!hasTimezone) iso = `${iso}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Interface para User
export interface User {
  userId: number;
  userName: string;
  userEmail: string;
  userPhone?: string;
  userRole?: number;
  roleId?: number;
  role_id?: number;
  userRoleName?: string;
  userTenant: number;
  tenant_id?: number;
  tenantId?: number;
  tenant?: Tenant;
  userStatus?: number; // 1 = ativo, 0 = inativo
  userLastLogin?: string; // Formato: "DD/MM/YYYY HH:MM"
  [key: string]: any;
}

// Interface para dados do usuário atual (logado)
interface CurrentUserData {
  isGeneralAdmin: boolean;
  userTenant: number | null;
}

// Tipo de acesso
type AccessType = 'general' | 'specific';

export default function UserList() {
  const { t, i18n } = useTranslation()
  const { navigateToPage } = useCryptoNavigation();
  
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [rolesMap, setRolesMap] = useState<{[key: number]: string}>({});
  const [openAccessModal, setOpenAccessModal] = useState(false);

  console.log("🔄 UserList render");

  // ✅ Obter configuração de acesso do Dashboard
  const getAccessConfig = () => {
    const accessType = (localStorage.getItem('accessType') as AccessType) || 'general';
    const selectedTenantId = localStorage.getItem('selectedTenantId');
    
    console.log('📋 Configuração de acesso:', {
      accessType,
      selectedTenantId
    });
    
    return {
      accessType,
      selectedTenantId: selectedTenantId ? Number(selectedTenantId) : null
    };
  };

  // ✅ Obter dados do usuário atual
  const getCurrentUserData = (): CurrentUserData => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        // ZERO preservado: ver normalizarId.
        const tenant = normalizarId(user.userTenant);
        return {
          isGeneralAdmin: tenant === null || user.isGeneralAdmin === true,
          userTenant: tenant
        };
      }
    } catch (error) {
      console.error("Erro ao ler dados do localStorage:", error);
    }

    return {
      isGeneralAdmin: true,
      userTenant: null
    };
  };

  const currentUser = getCurrentUserData();

  // Visão "por tenant": um proprietário específico está em escopo (selecionado no
  // topo ou usuário com tenant fixo). Aí a coluna Proprietário só repete o mesmo
  // valor em todas as linhas → escondemos.
  const isTenantScoped =
    (localStorage.getItem("accessType") || "general") !== "general" ||
    currentUser.userTenant != null;

  // Memoizar funções de navegação
  const handleNew = useCallback(() => {
    console.log("🚀 Navegando para novo usuário");
    navigateToPage('usuarios', { mode: 'new' });
  }, [navigateToPage]);

  const handleEdit = useCallback((userId: number | string) => {
    console.log("✏️ Navegando para editar usuário ID:", userId);
    navigateToPage('usuarios', { mode: 'edit', id: String(userId) });
  }, [navigateToPage]);

  // Carregar dados de roles para mapeamento
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        console.log("📊 Carregando roles...");
        const rolesResponse = await api.get("/role");
        
        let rolesData: any[] = [];
        if (Array.isArray(rolesResponse.data)) {
          rolesData = rolesResponse.data;
        } else if (rolesResponse.data && Array.isArray(rolesResponse.data.roles)) {
          rolesData = rolesResponse.data.roles;
        } else if (rolesResponse.data && typeof rolesResponse.data === 'object') {
          const possibleArrays = Object.values(rolesResponse.data).filter(Array.isArray);
          if (possibleArrays.length > 0) {
            rolesData = possibleArrays[0] as any[];
          }
        }
        
        const map: {[key: number]: string} = {};
        rolesData.forEach((role) => {
          const roleId = 
            role.id !== undefined ? role.id : 
            role.roleId !== undefined ? role.roleId : 
            role.role_id !== undefined ? role.role_id : null;
              
          const roleName = 
            role.name !== undefined ? role.name : 
            role.roleName !== undefined ? role.roleName : 
            role.role_name !== undefined ? role.role_name : null;
              
          if (roleId !== null && roleName !== null) {
            map[roleId] = roleName;
          }
        });
        
        console.log("✅ Roles carregados:", Object.keys(map).length);
        setRolesMap(map);
      } catch (error) {
        console.error("❌ Erro ao carregar roles:", error);
      }
    };

    fetchRoles();
  }, []);

  // Função para mostrar detalhes do usuário
  const handleShowDetails = useCallback(async (user: User) => {
    console.log("👁️ Visualizando detalhes do usuário:", user.userName);
    
    let userWithTenant = { ...user };
    
    const tenantId = user.userTenant || user.tenant_id || user.tenantId;
    if (tenantId && (!user.tenant || !user.tenant.tenantName)) {
      try {
        console.log("🔍 Buscando tenant para o usuário...");
        const response = await api.get(`/tenant/${tenantId}`);
        const tenantData = response.data.tenant || response.data;
        userWithTenant.tenant = tenantData;
        console.log("✅ Tenant encontrado:", tenantData);
      } catch (error) {
        console.error("❌ Erro ao buscar tenant:", error);
      }
    }
    
    setSelectedUser(userWithTenant);
    setOpenDetailsDialog(true);
  }, []);

  // Função para fechar modal de detalhes
  const handleCloseDetails = useCallback(() => {
    console.log("❌ Fechando modal de detalhes");
    setOpenDetailsDialog(false);
    setSelectedUser(null);
  }, []);

  // Função para deletar usuário
  const handleDelete = useCallback(async (userId: number) => {
    if (!window.confirm(t("users.confirmDelete"))) {
      return;
    }

    try {
      console.log("🗑️ Excluindo usuário:", userId);
      await api.delete(`/user/${userId}`);
      console.log("✅ Usuário excluído com sucesso");
      
      if (openDetailsDialog) {
        setOpenDetailsDialog(false);
      }
      
      setRefreshTrigger((prev) => prev + 1);
    } catch (err: any) {
      console.error("❌ Erro ao excluir usuário:", err);
      const errorMessage = err.response?.data?.message || "Erro desconhecido";
      alert(`Ocorreu um erro ao excluir o usuário: ${errorMessage}`);
    }
  }, [openDetailsDialog]);
  
  // Função para determinar a cor do chip de perfil
  const getRoleColor = useCallback((roleName: string) => {
    if (!roleName || roleName === "Desconhecido") return "default";
    
    const lowerRoleName = roleName.toLowerCase();
    if (lowerRoleName.includes("admin")) return "error";
    if (lowerRoleName.includes("gerente") || lowerRoleName.includes("manager")) return "warning";
    return "primary";
  }, []);

  // Função para obter o nome do perfil com base no ID
  const getRoleName = useCallback((roleId?: number, defaultName?: string) => {
    if (!roleId) return defaultName || "Desconhecido";
    return rolesMap[roleId] || defaultName || "Desconhecido";
  }, [rolesMap]);

  // Função para formatar data do último login
  const formatLastLogin = useCallback((lastLogin?: string | null) => {
    if (!lastLogin || lastLogin === '' || lastLogin === 'null') return " - ";
    
    try {
      // O backend envia o horário em UTC sem fuso explícito. parseServerDate
      // trata isso (formato BR e ISO) e o toLocaleString converte para o fuso
      // do usuário.
      const date = parseServerDate(lastLogin);
      if (!date) {
        console.warn('Data inválida recebida:', lastLogin);
        return " - ";
      }

      return date.toLocaleString(resolveDateLocale(i18n.language), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error, lastLogin);
      return " - ";
    }
  }, [i18n.language]);

  // Colunas para a tabela
  const columns = useMemo(() => [
    { 
      id: "userName", 
      label: t("fields.name"), 
      minWidth: 120
    },
    { 
      id: "userEmail", 
      label: t("fields.email"), 
      minWidth: 160,
      hide: isMobile
    },
    { 
      id: "userPhone", 
      label: t("fields.phone"), 
      minWidth: 130,
      format: (value: string) => value || "-",
      hide: isMobile || isTablet
    },
    {
      id: "userRole",
      label: t("fields.profile"),
      minWidth: 100,
      align: "center" as const,
      mobileHideLabel: true, // no card mobile, só a badge (sem o rótulo "Perfil")
      format: (value: number, row: any) => {
        const roleName = row.userRoleName || getRoleName(value, row.userRoleName);
        return (
          <Chip 
            label={roleName} 
            size="small" 
            color={getRoleColor(roleName)}
            variant="outlined"
          />
        );
      }
    },
    {
      // Parceiro a que o usuário pertence. O backend devolve userPartner e
      // userPartnerName; o nome é o que interessa, o id entra como reserva.
      // Zero é a operação principal, então NÃO use valor-verdade aqui.
      id: "userPartner",
      label: t("fields.partner"),
      minWidth: 120,
      hide: isMobile,
      format: (_value: unknown, row: any) => {
        const nome = row?.userPartnerName?.toString().trim()
        if (nome) return nome
        const id = normalizarId(row?.userPartner)
        return id === null ? "—" : String(id)
      },
    },
    {
      // Em quantos dispositivos este usuário recebe notificação.
      //
      // SEM a chave = não foi possível saber (a rota falhou). Com lista
      // vazia = ele realmente não recebe em lugar nenhum. Mostrar "0" nos
      // dois casos afirmaria como fato o que era falta de resposta.
      id: "pushTokens",
      label: t("users.notifications"),
      minWidth: 110,
      align: "center" as const,
      hide: isMobile,
      sortValue: (row: any) =>
        Array.isArray(row?.pushTokens) ? row.pushTokens.length : -1,
      format: (_value: unknown, row: any) => {
        const tokens = row?.pushTokens;
        if (!Array.isArray(tokens)) return "—";
        if (tokens.length === 0) {
          return (
            <Chip
              label={t("users.noDevices")}
              size="small"
              variant="outlined"
              color="default"
            />
          );
        }
        return (
          <Chip
            icon={<NotificationsActiveIcon />}
            label={tokens.length}
            size="small"
            variant="outlined"
            color="success"
          />
        );
      },
    },
    { 
      id: "userStatus", 
      label: t("fields.status"), 
      minWidth: 90,
      align: "center" as const,
      hide: isMobile || isTablet,
      format: (value: any) => {
        // Status: 1 = ativo, 0 = inativo
        const isActive = value === 1 || value === '1' || value === true;
        const status = isActive ? 'ativo' : 'inativo';
        
        return (
          <Chip 
            label={status.charAt(0).toUpperCase() + status.slice(1)} 
            size="small" 
            color={status === 'ativo' ? "success" : "default"}
            variant="outlined"
          />
        );
      }
    },
    { 
      id: "userLastLogin", 
      label: t("fields.lastLogin"), 
      minWidth: 130,
      align: "center" as const,
      hide: isMobile || isTablet,
      format: (value: any) => formatLastLogin(value)
    },
    {
      id: "tenant",
      label: t("fields.owner"),
      minWidth: 140,
      align: "center" as const,
      hide: isMobile || isTenantScoped, // some na visão por tenant (valor repetido)
      format: (value: any, row: any) => {
        const tenantName = row.tenant?.tenantName || null;
        return (
          <Chip 
            label={tenantName || t("users.notAssociated")} 
            size="small" 
            color={tenantName ? "success" : "default"}
            variant="outlined"
          />
        );
      }
    }
  ], [getRoleName, getRoleColor, formatLastLogin, isMobile, isTablet, isTenantScoped]);

  // Função para buscar informações de tenant para usuários
  const fetchTenantData = useCallback(async (users: User[]) => {
    try {
      // Busca TODOS os tenants uma vez e monta um mapa id→tenant. Antes era um
      // GET /tenant/:id por usuário (N+1) em paralelo; parte falhava e a badge do
      // proprietário ficava vazia mesmo com tenant associado (o detalhe, que faz
      // um único GET no clique, funcionava).
      const response = await api.get("/tenant");
      const raw = response.data;
      let list: Tenant[] = [];
      if (Array.isArray(raw)) {
        list = raw;
      } else if (Array.isArray(raw?.tenants)) {
        list = raw.tenants;
      } else if (raw && typeof raw === "object") {
        const arrays = Object.values(raw).filter(
          (v) => Array.isArray(v) && v.length > 0
        );
        if (arrays.length) list = arrays[0] as Tenant[];
      }

      const byId = new Map<number, Tenant>();
      for (const tn of list) {
        const tid = (tn as any).tenantId ?? (tn as any).id;
        if (tid != null) byId.set(Number(tid), tn);
      }

      return users.map((user) => {
        if (user.tenant?.tenantName) return user; // já veio embutido na lista
        const tenantId = normalizarId(user.userTenant, user.tenant_id, user.tenantId);
        if (tenantId === null) return user;
        const tenant = byId.get(Number(tenantId));
        return tenant ? { ...user, tenant } : user;
      });
    } catch (error) {
      console.error("❌ Erro ao buscar tenants:", error);
      return users;
    }
  }, []);

  // ✅ Função para aplicar filtro de tenant baseado na configuração do Dashboard
  const applyTenantFilter = useCallback((users: User[]): User[] => {
    const { accessType, selectedTenantId } = getAccessConfig();
    
    // Se não for admin geral, filtrar pelo tenant do usuário
    if (!currentUser.isGeneralAdmin && currentUser.userTenant) {
      console.log(`🔍 Filtrando por tenant do usuário: ${currentUser.userTenant}`);
      return users.filter(user => {
        const userTenantId = user.userTenant || user.tenant_id || user.tenantId;
        return userTenantId === currentUser.userTenant;
      });
    }

    // Se for admin geral e estiver em modo "specific" (tenant selecionado no Dashboard)
    if (currentUser.isGeneralAdmin && accessType === 'specific' && selectedTenantId) {
      console.log(`🔍 Filtrando por tenant selecionado no Dashboard: ${selectedTenantId}`);
      return users.filter(user => {
        const userTenantId = user.userTenant || user.tenant_id || user.tenantId;
        return userTenantId === selectedTenantId;
      });
    }

    // Se for admin geral e estiver em modo "general", mostrar todos
    console.log("🔍 Modo geral - Mostrando todos os usuários");
    return users;
  }, [currentUser]);

  // Função para processar os dados recebidos da API
  /**
   * Anexa a cada usuário os seus tokens de notificação.
   *
   * Uma chamada só para a lista inteira: a rota devolve todos os usuários, e
   * pedir por usuário seriam N requisições para a mesma informação.
   *
   * Em caso de FALHA os usuários voltam SEM a chave — e não com lista vazia.
   * São coisas diferentes: "não tem dispositivo" é um fato; "não deu para
   * saber" é ausência de resposta, e a coluna mostra cada um do seu jeito.
   */
  const anexarTokensDePush = useCallback(async (users: any[]): Promise<any[]> => {
    try {
      const resp = await api.get('/alert/get_push-tokens');
      const lista = Array.isArray(resp.data?.users) ? resp.data.users : [];

      const porUsuario = new Map<number, any[]>();
      for (const item of lista) {
        const id = normalizarId(item?.userId);
        if (id === null) continue;
        porUsuario.set(id, Array.isArray(item?.pushTokens) ? item.pushTokens : []);
      }

      return users.map((u) => {
        const id = normalizarId(u?.userId);
        return { ...u, pushTokens: (id !== null && porUsuario.get(id)) || [] };
      });
    } catch (err) {
      console.error('Erro ao buscar tokens de notificação:', err);
      return users;
    }
  }, []);

  const transformData = useCallback(async (data: any[]): Promise<User[]> => {
    console.log("🔄 Transformando dados dos usuários...");
    console.log("📊 Dados recebidos:", data);
    
    // Adicionar nome do perfil se não estiver presente
    let processedData = data.map((user: User) => {
      // Normaliza o último login para ISO (UTC) para que a ordenação da tabela
      // seja cronológica, e não alfabética sobre "DD/MM/YYYY HH:MM".
      const lastLoginDate = parseServerDate(user.userLastLogin);
      if (lastLoginDate) {
        user.userLastLogin = lastLoginDate.toISOString();
      }

      if (!user.userRoleName) {
        const roleId = 
          user.userRole !== undefined ? user.userRole : 
          user.roleId !== undefined ? user.roleId : 
          user.role_id !== undefined ? user.role_id : null;
        
        if (roleId !== null && rolesMap[roleId]) {
          user.userRoleName = rolesMap[roleId];
        } else {
          user.userRoleName = "Desconhecido";
        }
      }
      return user;
    });
    
    // Adicionar informações de tenant se necessário
    processedData = await fetchTenantData(processedData);

    processedData = await anexarTokensDePush(processedData);
    
    // ✅ Aplicar filtro de tenant baseado na configuração do Dashboard
    const filteredData = applyTenantFilter(processedData);
    
    console.log("✅ Dados transformados:", processedData.length);
    console.log("✅ Dados filtrados:", filteredData.length);
    console.log("📋 Amostra de dados:", filteredData[0]);
    return filteredData;
  }, [rolesMap, fetchTenantData, applyTenantFilter, anexarTokensDePush]);

  // Componente para exibir um campo de dados com label
  const DataField = ({ label, value }: { label: string, value: any }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
        {label}
      </Typography>
      <Typography variant="body1">
        {value || '-'}
      </Typography>
    </Box>
  );

  /* Quem administra vários grupos filtra pelo que NÃO tem preenchido:
         sem parceiro, filtra por parceiro; sem proprietário, por
         proprietário. Campos com o nome da PRÓPRIA entidade. */
  const filtrosDeGrupo = useMemo<FiltroCampo[]>(() => ([
      {
        id: "userPartner",
        label: t("fields.partner"),
        rotulo: (row: any) => row?.userPartnerName,
        exigeVazio: "partner",
      },
      {
        id: "userTenant",
        label: t("fields.owner"),
        rotulo: (row: any) => row?.tenant?.tenantName,
        exigeVazio: "tenant",
      },
    ]), [t])

  return (
    <>
      <EntityList
        title={t("nav.users")}
        endpoint="/user"
        columns={columns}
        addPath=""
        editPathPrefix=""
        idField="userId"
        dataPath="users"
        onRowClick={handleShowDetails}
        transformData={transformData}
      filterFields={filtrosDeGrupo}
        refreshTrigger={refreshTrigger}
        containerProps={{ maxWidth: "xl" }}
        useEncryptedNavigation={true}
        encryptedPageName="usuarios"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        entityType="users"
      />

      {/* Diálogo de Detalhes do Usuário */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedUser && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                Detalhes do Usuário: {selectedUser.userName}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleCloseDetails} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    Informações Básicas
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <DataField label={t("fields.id")} value={selectedUser.userId} />
                    <DataField label={t("fields.name")} value={selectedUser.userName} />
                    <DataField label={t("fields.email")} value={selectedUser.userEmail} />
                    <DataField label={t("fields.phone")} value={selectedUser.userPhone} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    Perfil e Associação
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        Perfil
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={selectedUser.userRoleName || getRoleName(selectedUser.userRole)} 
                          size="small" 
                          color={getRoleColor(selectedUser.userRoleName || "")}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={selectedUser.userStatus === 1 ? t("common.active") : t("common.inactive")} 
                          size="small" 
                          color={selectedUser.userStatus === 1 ? "success" : "default"}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <DataField 
                      label={t("fields.lastLogin")} 
                      value={formatLastLogin(selectedUser.userLastLogin)} 
                    />
                    <DataField label={t("fields.owner")} value={selectedUser.tenant?.tenantName || t("users.notAssociated")} />
                  </Box>
                </Grid>

                {/* Notificações: onde este usuário recebe. O token em si não
                    aparece — é identificador de envio, não informação útil
                    em tela; o que ajuda a diagnosticar é a plataforma e
                    quando foi atualizado pela última vez. */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    {t("users.notifications")}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {!Array.isArray((selectedUser as any).pushTokens) ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("users.devicesUnknown")}
                      </Typography>
                    ) : (selectedUser as any).pushTokens.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("users.noDevicesHelper")}
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {(selectedUser as any).pushTokens.map((tk: any) => (
                          <Box
                            key={tk?.tokenID ?? tk?.token}
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                          >
                            <Chip
                              icon={<NotificationsActiveIcon />}
                              label={tk?.tokenPlatform || "—"}
                              size="small"
                              variant="outlined"
                              color="success"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {t("users.deviceUpdated", {
                                date: tk?.tokenUpdated || "—",
                              })}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Button
                onClick={() => setOpenAccessModal(true)}
                color="primary"
              >
                {t('access.title')}
              </Button>
              
              <Button 
                onClick={handleCloseDetails}
              >
                Fechar
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  handleCloseDetails();
                  handleEdit(selectedUser.userId);
                }}
              >
                {t("common.edit")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Modal de Acesso */}
      <AccessModal
        open={openAccessModal}
        onClose={() => setOpenAccessModal(false)}
        userId={selectedUser?.userId}
      />
    </>
  );
}